import { rotuloRito } from '@/shared/constants/rito';
import {
  formatDate,
  formatDateLong,
  formatDateTimeAt,
  formatTime,
  TRACO,
} from '@/shared/formatters';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';
import { formatEndereco } from '@/shared/utils/endereco';
import type { MensagemStatus } from '@/shared/types/mensagemStatus';
import { negaAfirmacao } from '@/shared/utils/negacaoDeStatus';
import { ehUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import {
  EventoAcesso,
  getEventoAcessoLabel,
  getEventoAcessoVariant,
  getStatusEmailVariant,
  getStatusWhatsAppVariant,
  humanizeStatus,
  STATUS_CORREIOS_LABELS,
  STATUS_CORREIOS_VARIANTS,
  StatusEmail,
  StatusWhatsApp,
} from '@/shared/utils/statusComunicacao';
import type {
  AcessoPlataformaCertificado,
  CanalAuditoriaCertificado,
  CanalComunicacaoCertificado,
  CanalStatusParte,
  CartaCitacaoCertificado,
  CertificadoAcessosArbitral,
  DestinatarioAcessosPlataforma,
  DestinatarioAuditoriaCertificado,
  DestinatarioMensagemCertificado,
  EnvioCorreiosCertificado,
  EventoAuditoriaCertificado,
  GrupoPartesCertificado,
  MensagemEmailCertificado,
  MensagemWhatsappCertificado,
  ParteCertificadoAcessos,
  PartePainelComprovacao,
  RespostaWhatsappCertificado,
  ResumoComprovacao,
  TentativaAuditoria,
  VarianteStatusCertificado,
} from '../types/certificadoAcessosTypes';
import { linhasDaCitacao, seloDaCitacaoNaComprovacao } from './citacaoNaComprovacao';
import type {
  CanalAuditoriaDto,
  CanalAuditoriaParteDto,
  CanalDigitalCertificadoDto,
  CertificadoAcessosDto,
  ComprovacaoCanalDto,
  ComprovacaoParteDto,
  EnvioPostalDto,
  EventoCertificadoDto,
  ParteCertificadoDto,
  ReplicaComunicadoDto,
  RespostaDaParteDto,
  SituacaoComprovacaoParteDto,
  TentativaAuditoriaDto,
} from '../types/certificadoAcessosDto';
import { forcaStatusEntrega } from './statusEntregaComunicado';
import { rotuloStatusRcs, varianteStatusRcs } from './statusRcsCertificado';

const PAPEL_LABEL = { REQUERENTE: 'Requerente', REQUERIDO: 'Requerido' } as const;

/**
 * `CIENCIA_CONFIRMADA` entra nas duas listas porque a citação conversacional não passa pelo
 * pipeline de envio: o vocabulário dela é o da trilha, não o do provedor. Fora daqui, a tentativa
 * cuja parte confirmou a ciência saía AMARELA na Seção 4 enquanto a Seção 3 dizia "Confirmado", e
 * o contador de "responderam" ficava em zero num processo em que a parte respondeu — o mesmo
 * descompasso entre veredito e evidência que este documento existe para não ter.
 */
const TOKENS_CIENCIA = [
  'RESPONDIDO',
  'RESPONDED',
  'VISUALIZADO',
  'READ',
  'SEEN',
  'CIENCIA_CONFIRMADA',
];
const TOKENS_RESPOSTA = ['RESPONDIDO', 'RESPONDED', 'CIENCIA_CONFIRMADA'];

function contemToken(status: string | null, tokens: string[]): boolean {
  if (!status) return false;
  const s = status.toUpperCase();
  return tokens.some((t) => s.includes(t));
}

// `contemToken` casa por substring; a negação (`NOT_DELIVERED`, `UNREAD`) só barra as listas
// positivas — a de falha contém `UNDELIVERABLE`, que é afirmação de falha. Ver `negaAfirmacao`.
function afirmaToken(status: string | null, tokens: string[]): boolean {
  return !!status && !negaAfirmacao(status) && contemToken(status, tokens);
}

const ehCiencia = (status: string | null): boolean => afirmaToken(status, TOKENS_CIENCIA);
const ehResposta = (status: string | null): boolean => afirmaToken(status, TOKENS_RESPOSTA);

function varianteDigital(
  canal: CanalDigitalCertificadoDto,
  status: string | null,
): VarianteStatusCertificado | undefined {
  if (!status) return undefined;
  if (canal === 'RCS') return varianteStatusRcs(status);
  const variant =
    canal === 'EMAIL'
      ? status in StatusEmail
        ? getStatusEmailVariant(status as StatusEmail)
        : undefined
      : status in StatusWhatsApp
        ? getStatusWhatsAppVariant(status as StatusWhatsApp)
        : undefined;
  if (!variant) return undefined;
  return variant === 'brand' ? 'neutral' : variant;
}

const ehFalhaCanonica = (canal: CanalDigitalCertificadoDto, status: string | null): boolean =>
  varianteDigital(canal, status) === 'error';

function varianteCorreios(status: string | null): VarianteStatusCertificado | undefined {
  if (!status) return undefined;
  const v = STATUS_CORREIOS_VARIANTS[status];
  if (!v) return undefined;
  return v === 'brand' ? 'neutral' : v;
}

function urlExibicao(urlAbsoluta: string): string {
  try {
    const url = new URL(urlAbsoluta);
    return `${url.host}${url.pathname}`;
  } catch {
    return urlAbsoluta;
  }
}

/**
 * A URL de validação vira `href` de uma âncora num documento lido por partes externas, e React não
 * neutraliza `javascript:` em `href`. Esquema fora de `http(s)` é tratado como ausência — o
 * documento então avisa que a conferência não está disponível, em vez de oferecer o sink.
 *
 * Exige URL **absoluta**, mais estrito que o utilitário compartilhado (que aceita caminho relativo
 * por servir também a navegação interna): aqui o destino é IMPRESSO, e `/autenticar?code=…` num PDF
 * na mão do leitor não é endereço que se digite em lugar nenhum.
 *
 * Rejeitar valor NÃO-NULO é sinal de `APP_BASE_URL` mal configurada no back, não estado de negócio:
 * sem telemetria, o certificado passa a imprimir o código na capa e a negar a conferência três
 * linhas abaixo, em silêncio. `null` continua mudo — ali a ausência é legítima.
 */
function urlValidacaoSegura(url: string | null): string | null {
  if (url === null) return null;
  if (ehUrlNavegavelSegura(url) && /^https?:\/\//i.test(url)) return url;
  registrarUrlValidacaoRejeitada();
  return null;
}

/** Sem o valor da URL: ela carrega o código de autenticação do certificado. */
function registrarUrlValidacaoRejeitada(): void {
  if (typeof window === 'undefined') return;
  void import('@datadog/browser-rum')
    .then(({ datadogRum }) => {
      datadogRum.addAction('certificadoAcessos.urlValidacaoRejeitada', {
        feature: 'certificadoAcessos',
      });
    })
    .catch(() => {});
}

// ————— Partes —————

function prefixoDocumento(tipoPessoa: string | null, documento: string | null): string {
  if (!documento) return '';
  const prefixo = tipoPessoa === 'JURIDICA' ? 'CNPJ' : 'CPF';
  return `${prefixo} ${documento}`;
}

function contatosParte(parte: ParteCertificadoDto): string[] {
  return [...parte.emails, ...parte.telefones.map((t) => formatTelefoneComCodigoPais(t))];
}

function parteParaGrupo(parte: ParteCertificadoDto, papelLabel: string): ParteCertificadoAcessos {
  return {
    nome: parte.nome,
    papelLabel,
    papelDestacado: true,
    contatos: contatosParte(parte),
    documento: prefixoDocumento(parte.tipoPessoa, parte.documento),
  };
}

function advogadosDaParte(parte: ParteCertificadoDto): ParteCertificadoAcessos[] {
  return parte.advogados.map((adv) => ({
    nome: adv.nome,
    papelLabel: 'Advogado',
    papelDestacado: false,
    contatos: [],
    documento: adv.oab ? `OAB ${adv.oab}` : '',
  }));
}

function montarGrupo(
  titulo: string,
  corDestaque: 'brand' | 'coral',
  partes: ParteCertificadoDto[],
  papelLabel: string,
): GrupoPartesCertificado {
  const linhas = partes.flatMap((p) => [parteParaGrupo(p, papelLabel), ...advogadosDaParte(p)]);
  return { titulo, corDestaque, partes: linhas };
}

function montarGrupos(dto: CertificadoAcessosDto): GrupoPartesCertificado[] {
  const grupos: GrupoPartesCertificado[] = [];
  if (dto.partes.requerentes.length > 0) {
    grupos.push(montarGrupo('Parte Requerente', 'brand', dto.partes.requerentes, 'Requerente'));
  }
  if (dto.partes.requeridos.length > 0) {
    grupos.push(montarGrupo('Parte Requerida', 'coral', dto.partes.requeridos, 'Requerido'));
  }
  return grupos;
}

// ————— Comprovação (resumo por parte) —————

function labelStatusCanal(canal: CanalComunicacaoCertificado, status: string | null): string {
  if (!status) return TRACO;
  if (canal === 'CORREIOS') return STATUS_CORREIOS_LABELS[status] ?? status;
  if (canal === 'RCS') return rotuloStatusRcs(status);
  return humanizeStatus(status, canal);
}

function varianteComprovacao(canal: ComprovacaoCanalDto): VarianteStatusCertificado {
  if (canal.canal === 'CORREIOS') return varianteCorreios(canal.status) ?? 'neutral';
  if (canal.suficiente) return 'success';
  if (canal.canal !== 'PLATAFORMA' && ehFalhaCanonica(canal.canal, canal.status)) return 'error';
  if (canal.status) return 'warning';
  return 'neutral';
}

/**
 * Um rótulo por GRAU de acesso registrado — nunca um "Acessou" que cubra todos.
 *
 * `ACESSO_REGISTRADO` é a abertura do link ANTES de qualquer autenticação: anônima, e por isso
 * deliberadamente FORA da régua de suficiência do back (`comprovacaoCienciaService.ts`,
 * `STATUS_SUFICIENTES.PLATAFORMA` = `ABERTURA_VALIDADA` | `CONTEUDO_VISUALIZADO` |
 * `DOCUMENTO_BAIXADO`). Colapsar todo status não nulo em "Acessou" fazia esta tela afirmar ciência
 * sobre um fato que o próprio back classifica como insuficiente, e contradizer o PDF emitido pelo
 * back, que imprime o status cru ao lado de uma coluna "Suficiente" — o descompasso entre veredito
 * e evidência que este documento existe para não ter.
 *
 * Os quatro primeiros textos são os do back (`eventoTranslation.ts`), que é o que a seção "Acessos
 * na Plataforma" deste mesmo documento já exibe no campo `evento`: um evento, um nome em todo o
 * certificado. Os dois que o back não traduz reusam o rótulo que este front já publica, em vez de
 * inaugurar microcopy nova numa peça de prova.
 */
const LABEL_ACESSO_PLATAFORMA: Record<EventoAcesso, string> = {
  [EventoAcesso.ACESSO_REGISTRADO]: 'Abriu o link de acesso',
  [EventoAcesso.ABERTURA_VALIDADA]: 'Acesso ao portal validado',
  [EventoAcesso.CONTEUDO_VISUALIZADO]: 'Visualizou o processo no portal',
  [EventoAcesso.DOCUMENTO_BAIXADO]: 'Baixou documento do processo',
  [EventoAcesso.TENTATIVA_ACESSO_FALHA]: getEventoAcessoLabel(EventoAcesso.TENTATIVA_ACESSO_FALHA),
  [EventoAcesso.QRCODE_ESCANEADO]: getEventoAcessoLabel(EventoAcesso.QRCODE_ESCANEADO),
};

/**
 * Grau que este front ainda não conhece sai CRU, como no back (`traduzirEvento` devolve o valor
 * quando não o traduz): o contrato chega como `string`, e um valor novo do enum no back antes do
 * deploy do front cairia aqui. Escolher um rótulo amigável para grau desconhecido reintroduziria,
 * em silêncio, exatamente o defeito que este mapa corrige.
 */
function labelAcessoPlataforma(status: string | null): string {
  if (!status) return 'Sem acesso';
  return status in EventoAcesso ? LABEL_ACESSO_PLATAFORMA[status as EventoAcesso] : status;
}

function labelCanalComprovacao(canal: ComprovacaoCanalDto): string {
  if (canal.canal === 'PLATAFORMA') return labelAcessoPlataforma(canal.status);
  return labelStatusCanal(canal.canal, canal.status);
}

function canalComprovacao(canal: ComprovacaoCanalDto): CanalStatusParte {
  return {
    canal: canal.canal as CanalComunicacaoCertificado,
    statusLabel: labelCanalComprovacao(canal),
    variante: varianteComprovacao(canal),
  };
}

// Mesmo rótulo que o PDF do back imprime na seção 3 (`certificadoAcessosPdfTemplate`): tela e
// peça emitida não podem divergir sobre a situação da parte.
const SITUACAO_COMPROVACAO: Record<
  SituacaoComprovacaoParteDto,
  { label: string; variante: VarianteStatusCertificado }
> = {
  CONFIRMADO: { label: 'Confirmado', variante: 'success' },
  PENDENTE: { label: 'Pendente', variante: 'warning' },
  IDENTIDADE_REJEITADA: { label: 'Identidade não confirmada', variante: 'error' },
};

/**
 * O RCS só existe nas citações com etapa final. Sem grau registrado, a célula não afirma fato
 * nenhum — e, se o core declarar o canal para toda parte, como faz com os outros, a Seção 3 de todo
 * certificado anterior à régua ganharia uma coluna inteira de traços.
 */
function canalComRegistroNaParte(canal: ComprovacaoCanalDto): boolean {
  return canal.canal !== 'RCS' || canal.status !== null;
}

/**
 * Na Seção 3 da CITAÇÃO, o visto do WhatsApp é indício (cinza) e o e-mail lido é o clique no link
 * (D-04). O Certificado da Sentença atravessa este mesmo mapper sem `citacao`, e lá o visto é grau
 * válido de "lida": fica como sempre. `statusComunicacao` não muda — o extrajudicial, a régua de
 * cobrança e as Seções 5 e 6 leem o mesmo mapa.
 */
const CANAL_NA_CITACAO: Partial<
  Record<string, { label: string; variante?: VarianteStatusCertificado }>
> = {
  'WHATSAPP:VISUALIZADO': { label: 'Visto no WhatsApp (indício)', variante: 'neutral' },
  'EMAIL:VISUALIZADO': { label: 'Clicou no link do e-mail' },
};

function canalNaCitacao(canal: ComprovacaoCanalDto): CanalStatusParte {
  const base = canalComprovacao(canal);
  const naCitacao = CANAL_NA_CITACAO[`${canal.canal}:${canal.status}`];
  if (!naCitacao) return base;
  return { ...base, statusLabel: naCitacao.label, variante: naCitacao.variante ?? base.variante };
}

function partePainel(parte: ComprovacaoParteDto): PartePainelComprovacao {
  // Sem o resumo da citação (Certificado da Sentença, core anterior à D-04), o selo da comprovação.
  const situacao = seloDaCitacaoNaComprovacao(parte) ?? SITUACAO_COMPROVACAO[parte.situacao];
  const daCitacao = parte.citacao ? canalNaCitacao : canalComprovacao;
  return {
    nome: parte.nome,
    documento: parte.documento ?? '',
    situacaoLabel: situacao.label,
    situacaoVariante: situacao.variante,
    canais: parte.canais.filter(canalComRegistroNaParte).map(daCitacao),
    linhasDaCitacao: linhasDaCitacao(parte.citacao),
  };
}

function montarResumo(comprovacao: ComprovacaoParteDto[]): ResumoComprovacao {
  const respondeu = (p: ComprovacaoParteDto) => p.canais.some((c) => ehResposta(c.status));
  return {
    totalPartes: comprovacao.length,
    partesComunicadas: comprovacao.filter((p) => p.situacao === 'CONFIRMADO').length,
    partesResponderam: comprovacao.filter(respondeu).length,
    partes: comprovacao.map(partePainel),
  };
}

// ————— Auditoria (tentativas + eventos) —————

const COLUNA_DETALHE: Record<CanalAuditoriaDto['canal'], string> = {
  WHATSAPP: 'Detalhes',
  EMAIL: 'Data e hora',
  RCS: 'Detalhes',
  CORREIOS: 'Detalhes',
};

function varianteResultado(
  canal: CanalAuditoriaDto['canal'],
  status: string | null,
): VarianteStatusCertificado {
  if (!status) return 'neutral';
  if (canal === 'CORREIOS') return varianteCorreios(status) ?? 'neutral';
  if (ehFalhaCanonica(canal, status)) return 'error';
  // No RCS a leitura é indício e não ciência: só o grau da régua própria diz o que comprova.
  if (canal === 'RCS') return varianteStatusRcs(status) ?? 'warning';
  return ehCiencia(status) ? 'success' : 'warning';
}

function eventoSucesso(canal: CanalAuditoriaDto['canal'], statusRaw: string): boolean {
  if (canal === 'CORREIOS') return varianteCorreios(statusRaw) === 'success';
  return !ehFalhaCanonica(canal, statusRaw);
}

function labelResultado(canal: CanalAuditoriaDto['canal'], status: string | null): string {
  return labelStatusCanal(canal, status);
}

function lerMotivo(detalhes: Record<string, unknown> | null): string | null {
  const motivo = detalhes?.motivo;
  return typeof motivo === 'string' && motivo.trim() ? motivo : null;
}

/**
 * Transcrição da resposta da parte, quando o evento é um ato dela.
 *
 * Sem esta linha o documento certificava "Respondeu no WhatsApp" e omitia o que foi respondido —
 * que é justamente o que mostra COMO a ciência ocorreu.
 *
 * `direction === 'inbound'` é EXIGIDO, não inferido de `texto` existir (COR-01 da review): o nó
 * `arbitral.append_certificate_event` grava `detalhesExtras`, um `Record` autorado livre no
 * editor, então nada impede que um evento OUTBOUND (`M02_ABORDAGEM_INICIAL` é o candidato óbvio)
 * passe a carregar `texto`. Sem o discriminador, a fala da câmara sairia entre aspas e em
 * itálico, indistinguível de uma transcrição do notificado — atribuir a fala de um ao outro é o
 * pior modo de falha desta tela. Mesma régua que o back aplica no leitor da comprovação.
 */
function lerResposta(detalhes: Record<string, unknown> | null): string | null {
  if (detalhes?.direction !== 'inbound') return null;
  const texto = detalhes.texto;
  return typeof texto === 'string' && texto.trim() ? texto.trim() : null;
}

function metadadosEvento(
  evento: EventoCertificadoDto,
  sucesso: boolean,
): EventoAuditoriaCertificado['metadados'] {
  const metadados: EventoAuditoriaCertificado['metadados'] = [];
  if (evento.messageId) metadados.push({ rotulo: 'ID', valor: evento.messageId });
  // Hash INTEIRO: o truncado (13 dos 64 caracteres) era decorativo — não dá para recomputar o
  // SHA-256 de um evento nem casar o elo com o topo impresso na Seção 4 a partir de um prefixo.
  // Num documento cuja finalidade declarada é permitir perícia independente, imprimir o resumo de
  // um resumo é oferecer a conferência sem os dados dela.
  if (evento.hashSha256) {
    metadados.push({ rotulo: 'Hash SHA-256', valor: evento.hashSha256 });
  }
  const motivo = lerMotivo(evento.detalhes);
  if (!sucesso && motivo) metadados.push({ rotulo: 'Motivo', valor: motivo });
  return metadados;
}

/**
 * O `evento` do core é o tradutor genérico, que devolve o próprio código quando não o conhece: o
 * `ENTREGUE` da trilha chegava cru na tela. No RCS ele ainda erra o sentido — traduz o `CLICKED`
 * como "Link clicado", o do e-mail —, então ali manda só a régua própria.
 */
function rotuloDoEvento(canal: CanalAuditoriaDto['canal'], evento: EventoCertificadoDto): string {
  if (canal === 'RCS') return labelStatusCanal(canal, evento.eventoOriginal);
  const traduzidoPeloCore = evento.evento && evento.evento !== evento.eventoOriginal;
  return traduzidoPeloCore ? evento.evento : labelStatusCanal(canal, evento.eventoOriginal);
}

function eventoAuditoria(
  canal: CanalAuditoriaDto['canal'],
  evento: EventoCertificadoDto,
): EventoAuditoriaCertificado {
  const sucesso = eventoSucesso(canal, evento.eventoOriginal);
  return {
    statusLabel: rotuloDoEvento(canal, evento),
    sucesso,
    detalheLabel: formatDateTimeAt(evento.dataHora),
    respostaLabel: lerResposta(evento.detalhes),
    metadados: metadadosEvento(evento, sucesso),
  };
}

function tentativaAuditoria(
  canal: CanalAuditoriaDto['canal'],
  tentativa: TentativaAuditoriaDto,
): TentativaAuditoria {
  return {
    indice: tentativa.numero,
    tentativaLabel: canal === 'CORREIOS' ? 'Postagem' : `${tentativa.numero}ª tentativa`,
    dataLabel: formatDate(tentativa.iniciadaEm),
    resultadoLabel: labelResultado(canal, tentativa.resultado),
    variante: varianteResultado(canal, tentativa.resultado),
    eventos: tentativa.eventos.map((e) => eventoAuditoria(canal, e)),
  };
}

function destinatarioAuditoria(
  canal: CanalAuditoriaDto['canal'],
  parte: CanalAuditoriaParteDto,
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
): DestinatarioAuditoriaCertificado {
  const info = parteInfo.get(parte.parteArbitralId);
  return {
    nome: info?.nome ?? '',
    papelLabel: info?.papelLabel ?? PAPEL_LABEL.REQUERIDO,
    contatoLabel: parte.contato ?? '',
    tentativas: parte.tentativas.map((t) => tentativaAuditoria(canal, t)),
  };
}

function montarAuditoria(
  auditoria: CanalAuditoriaDto[],
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
): CanalAuditoriaCertificado[] {
  return auditoria.map((canal) => ({
    canal: canal.canal as CanalComunicacaoCertificado,
    colunaDetalheTitulo: COLUNA_DETALHE[canal.canal],
    incluiMetadados: canal.canal !== 'CORREIOS',
    destinatarios: canal.partes.map((p) => destinatarioAuditoria(canal.canal, p, parteInfo)),
  }));
}

// ————— Acessos à plataforma —————

function dispositivoLabel(evento: EventoCertificadoDto): string | null {
  const partes = [
    evento.navegador,
    evento.sistemaOperacional,
    evento.ip ? `IP ${evento.ip}` : null,
  ].filter((p): p is string => Boolean(p));
  return partes.length > 0 ? partes.join(' · ') : null;
}

function coordenadaLegivel(valor: number): string {
  return String(Number(valor.toFixed(6)));
}

function municipioLabel(evento: EventoCertificadoDto): string | null {
  const { cidade, estado } = evento.localizacao ?? {};
  return cidade && estado ? `${cidade}/${estado}` : null;
}

function coordenadasLabel(evento: EventoCertificadoDto): string | null {
  const localizacao = evento.localizacao;
  if (!localizacao) return null;
  return `${coordenadaLegivel(localizacao.latitude)}, ${coordenadaLegivel(localizacao.longitude)}`;
}

function acessoPlataforma(evento: EventoCertificadoDto): AcessoPlataformaCertificado {
  const detalhe = [formatDateTimeAt(evento.dataHora), `Log ${evento.id}`]
    .filter(Boolean)
    .join(' | ');
  const variante =
    evento.eventoOriginal in EventoAcesso
      ? getEventoAcessoVariant(evento.eventoOriginal as EventoAcesso)
      : undefined;
  return {
    statusLabel: evento.evento || evento.eventoOriginal,
    sucesso: variante !== 'error',
    detalheLabel: detalhe,
    dispositivoLabel: dispositivoLabel(evento),
    municipioLabel: municipioLabel(evento),
    coordenadasLabel: coordenadasLabel(evento),
  };
}

function montarAcessos(
  dto: CertificadoAcessosDto,
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
): DestinatarioAcessosPlataforma[] {
  return dto.acessosPlataforma.map((parte) => {
    const info = parteInfo.get(parte.parteArbitralId);
    return {
      destinatarioLabel: `${info?.papelLabel ?? PAPEL_LABEL.REQUERIDO}: ${info?.nome ?? ''}`,
      eventos: parte.acessos.map(acessoPlataforma),
    };
  });
}

// ————— Mensagens (WhatsApp / e-mail) —————

interface LinhaDestinatario {
  replica: ReplicaComunicadoDto;
  contato: string;
}

/**
 * Preferência ENTRE réplicas, que não é a escala de força de uma mensagem só. Dentro de uma
 * mensagem, `FAILED` é o piso (falha tardia não desfaz entrega); entre mensagens, uma falha
 * confirmada é um fato e `PENDING` (inclusive `NOT_DELIVERED`, que não diz se falhou) é ausência de
 * resolução — a linha não pode trocar o fato pela ausência.
 */
const PREFERENCIA_DA_REPLICA: Record<MensagemStatus, number> = {
  PENDING: 0,
  FAILED: 1,
  SENT: 2,
  DELIVERED: 3,
  SEEN: 4,
};
const SEM_AVISO = -1;

/**
 * Uma linha por parte e contato dentro do card, mesmo quando o mesmo texto saiu mais de uma vez para
 * ele na MESMA tentativa (reenvio de outra tentativa vira card próprio, ver `agruparPorConteudo`). A
 * linha fica com a réplica cujo aviso do provedor é o MAIS FORTE — e exibe o id e o status DESSA
 * mensagem, então o par continua sendo o fato de um envio só. Ficar com a primeira réplica mostrava
 * "—" quando só o reenvio tinha aviso: a entrega real sumia da peça.
 */
function linhasDestinatarioUnicas(
  grupo: ReplicaComunicadoDto[],
  canal: 'WHATSAPP' | 'EMAIL',
  statusPorMessageId: Map<string, string>,
): LinhaDestinatario[] {
  const forcaDaReplica = (replica: ReplicaComunicadoDto): number => {
    const status = replica.messageId ? statusPorMessageId.get(replica.messageId) : undefined;
    return status ? PREFERENCIA_DA_REPLICA[statusEntregaDeResultado(canal, status)] : SEM_AVISO;
  };
  const porContato = new Map<string, LinhaDestinatario>();
  for (const replica of grupo) {
    for (const contato of replica.destinatarios) {
      const chave = `${replica.parteArbitralId}:${contato}`;
      const atual = porContato.get(chave);
      if (!atual || forcaDaReplica(replica) > forcaDaReplica(atual.replica)) {
        porContato.set(chave, { replica, contato });
      }
    }
  }
  return [...porContato.values()];
}

/**
 * O status de CADA mensagem da Seção 6: o maior degrau entre os eventos da trilha com o
 * `messageId` dela.
 *
 * A intenção sempre foi o status de entrega daquele envio (a troca de 03/08 tirou o agregado por
 * parte justamente para uma primeira tentativa falha não herdar o sucesso da seguinte). Casava
 * pela TENTATIVA e imprimia o resultado dela, o que dava o mesmo número enquanto cada tentativa
 * tinha um envio só por canal. A citação conversacional manda várias mensagens na mesma tentativa,
 * e o resultado da tentativa virou o da citação: "Ciência confirmada" na linha da abordagem inicial,
 * que foi enviada antes de qualquer resposta.
 *
 * Só avisos do provedor carregam `messageId` (a resposta e a ciência não), então os eventos de uma
 * mensagem são os graus de entrega dela.
 */
function indexarStatusPorMessageId(auditoria: CanalAuditoriaDto[]): Map<string, string> {
  const indice = new Map<string, string>();
  for (const canalAuditoria of auditoria) {
    const { canal } = canalAuditoria;
    if (canal === 'CORREIOS') continue;
    const forca = (status: string) => forcaStatusEntrega(statusEntregaDeResultado(canal, status));
    for (const parte of canalAuditoria.partes) {
      for (const tentativa of parte.tentativas) {
        for (const evento of tentativa.eventos) {
          if (!evento.messageId) continue;
          const atual = indice.get(evento.messageId);
          if (atual === undefined || forca(evento.eventoOriginal) >= forca(atual)) {
            indice.set(evento.messageId, evento.eventoOriginal);
          }
        }
      }
    }
  }
  return indice;
}

/**
 * Tokens crus que o provedor manda e que os enums `StatusEmail`/`StatusWhatsApp` não cobrem —
 * `UNDELIVERABLE_NOT_DELIVERED` e companhia chegam como `resultado` da tentativa. Checados ANTES
 * da entrega justamente porque `UNDELIVERABLE_NOT_DELIVERED` contém `DELIVERED`.
 */
const TOKENS_FALHA_BRUTA = [
  'UNDELIVERABLE',
  'BOUNCE',
  'REJECT',
  'FAIL',
  'FALHA',
  'SPAM',
  'COMPLAINT',
  'BLOQUEAD',
];
const TOKENS_ENTREGA = ['DELIVERED', 'ENTREGUE'];
const TOKENS_ENVIO = ['SENT', 'ENVIADO'];

/**
 * Status cru do provedor no vocabulário do ícone de entrega.
 *
 * Grau que não bate com nenhuma regra cai em `PENDING`, o mais fraco que ainda não é negativa:
 * esta é a fonte de um ícone que, em peça de prova, AFIRMA algo sobre a comunicação. Escolher um
 * degrau mais forte para token desconhecido é exatamente o defeito que se está corrigindo — o ✓✓
 * azul fixo afirmava leitura sem nenhum dado por trás.
 */
function statusEntregaDeResultado(
  canal: CanalDigitalCertificadoDto,
  status: string | null,
): MensagemStatus {
  if (!status) return 'PENDING';
  if (ehFalhaCanonica(canal, status) || contemToken(status, TOKENS_FALHA_BRUTA)) return 'FAILED';
  if (ehCiencia(status)) return 'SEEN';
  if (afirmaToken(status, TOKENS_ENTREGA)) return 'DELIVERED';
  if (afirmaToken(status, TOKENS_ENVIO)) return 'SENT';
  // Negado sem token de falha (`NOT_DELIVERED`) cai aqui, em PENDING — e não em FAILED: "não
  // entregue" não diz se falhou ou se ainda não aconteceu, e escolher FAILED seria outra
  // afirmação sem dado por trás.
  return 'PENDING';
}

const SEM_AVISO_DO_PROVEDOR: Pick<
  DestinatarioMensagemCertificado,
  'statusLabel' | 'sucesso' | 'statusEntrega'
> = { statusLabel: TRACO, sucesso: false, statusEntrega: 'PENDING' };

/**
 * Status de UMA mensagem, e só do que o provedor avisou sobre ela.
 *
 * Mensagem sem aviso próprio herdava o status agregado do canal da parte, e no certificado
 * probatório isso afirmava entrega que não aconteceu: no ARB-202609-000006 os três lembretes
 * recusados pela Meta (`EC_INVALID_TEMPLATE`) apareciam "Visualizado", porque a abertura tinha sido
 * lida. Sem aviso, a linha não afirma entrega.
 *
 * Mas afirma o envio: o id da mensagem só existe quando o provedor ACEITOU o despacho (no core,
 * `carregarEnviosDoFluxo` e `arbitralEnvioDispatchService`), então ela saiu. Sem isto a linha
 * ficava "—" com o ponto de falha, e a citação aceita e ainda não entregue lia como não enviada
 * (ARB-26-J4GHW2, 03/11/2026).
 */
const STATUS_DO_DESPACHO_ACEITO = 'ENVIADO';

function statusDestinatario(
  messageId: string | null,
  statusPorMessageId: Map<string, string>,
  canal: 'WHATSAPP' | 'EMAIL',
): Pick<DestinatarioMensagemCertificado, 'statusLabel' | 'sucesso' | 'statusEntrega'> {
  if (!messageId) return SEM_AVISO_DO_PROVEDOR;
  const daMensagem = statusPorMessageId.get(messageId) ?? STATUS_DO_DESPACHO_ACEITO;
  return {
    statusLabel: labelStatusCanal(canal, daMensagem),
    sucesso: !ehFalhaCanonica(canal, daMensagem),
    statusEntrega: statusEntregaDeResultado(canal, daMensagem),
  };
}

function destinatarioMensagem(
  { replica, contato }: LinhaDestinatario,
  canal: 'WHATSAPP' | 'EMAIL',
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
  statusPorMessageId: Map<string, string>,
): DestinatarioMensagemCertificado {
  return {
    parteArbitralId: replica.parteArbitralId,
    enviadoEm: replica.enviadoEm,
    nome: parteInfo.get(replica.parteArbitralId)?.nome ?? '',
    contato: canal === 'WHATSAPP' ? formatTelefoneComCodigoPais(contato) : contato,
    idMensagem: replica.messageId ?? '',
    chave: `${replica.parteArbitralId}:${contato}`,
    ...statusDestinatario(replica.messageId, statusPorMessageId, canal),
  };
}

/**
 * Um card por texto E por tentativa. O card imprime UMA data para as linhas de baixo, e cada linha
 * imprime o id e o status de uma mensagem: juntar tentativas no mesmo card punha o reenvio de 17/07
 * embaixo de "Data: 15 de julho" (COR-04 da review do #1186). Na mesma tentativa, a data é a do
 * primeiro envio dela.
 */
function agruparPorConteudo(
  replicas: ReplicaComunicadoDto[],
  conteudo: (r: ReplicaComunicadoDto) => string,
): ReplicaComunicadoDto[][] {
  const grupos = new Map<string, ReplicaComunicadoDto[]>();
  for (const r of replicas) {
    const k = `${r.tentativa}\n${conteudo(r)}`;
    const g = grupos.get(k);
    if (g) g.push(r);
    else grupos.set(k, [r]);
  }
  return [...grupos.values()];
}

function grupoParaWhatsapp(
  grupo: ReplicaComunicadoDto[],
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
  statusPorMessageId: Map<string, string>,
  de: string,
): MensagemWhatsappCertificado {
  const primeira = grupo[0];
  return {
    de,
    dataLabel: formatDateLong(primeira?.enviadoEm ?? null),
    destinatarios: linhasDestinatarioUnicas(grupo, 'WHATSAPP', statusPorMessageId).map((linha) =>
      destinatarioMensagem(linha, 'WHATSAPP', parteInfo, statusPorMessageId),
    ),
    corpo: primeira?.conteudoTexto ?? '',
    horaLabel: formatTime(primeira?.enviadoEm ?? null),
  };
}

const RESSALVA_TRANSCRICAO = 'transcrição de áudio';
const RESSALVA_TRECHO = 'trecho inicial da mensagem';

/**
 * Sem a ressalva, quem lê não distingue o que a parte digitou do que o modelo ouviu num áudio, nem o
 * texto inteiro de um trecho. Mesmo texto do PDF do servidor.
 */
function ressalvaDaResposta(resposta: RespostaDaParteDto): string | null {
  const ressalvas = [
    resposta.textoTranscrito ? RESSALVA_TRANSCRICAO : null,
    resposta.textoTruncado ? RESSALVA_TRECHO : null,
  ].filter((r): r is string => r !== null);
  return ressalvas.length > 0 ? ressalvas.join('; ') : null;
}

function montarRespostasWhatsapp(
  respostas: RespostaDaParteDto[],
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
): RespostaWhatsappCertificado[] {
  return respostas.map((resposta) => ({
    parteArbitralId: resposta.parteArbitralId,
    nome: parteInfo.get(resposta.parteArbitralId)?.nome ?? '',
    contato: resposta.contato ? formatTelefoneComCodigoPais(resposta.contato) : '',
    corpo: resposta.texto,
    recebidaEm: resposta.recebidaEm,
    dataLabel: formatDateLong(resposta.recebidaEm),
    horaLabel: formatTime(resposta.recebidaEm),
    idMensagem: resposta.messageId ?? '',
    ressalva: ressalvaDaResposta(resposta),
  }));
}

function montarMensagensWhatsapp(
  replicas: ReplicaComunicadoDto[],
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
  statusPorMessageId: Map<string, string>,
  de: string,
): MensagemWhatsappCertificado[] {
  const doCanal = replicas.filter((r) => r.canal === 'WHATSAPP');
  return agruparPorConteudo(doCanal, (r) => r.conteudoTexto ?? '').map((grupo) =>
    grupoParaWhatsapp(grupo, parteInfo, statusPorMessageId, de),
  );
}

function grupoParaEmail(
  grupo: ReplicaComunicadoDto[],
  inst: CertificadoAcessosDto['instituicao'],
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
  statusPorMessageId: Map<string, string>,
): MensagemEmailCertificado {
  const primeira = grupo[0];
  return {
    de: inst.orgao,
    assunto: primeira?.assunto ?? '',
    dataLabel: formatDateLong(primeira?.enviadoEm ?? null),
    destinatarios: linhasDestinatarioUnicas(grupo, 'EMAIL', statusPorMessageId).map((linha) =>
      destinatarioMensagem(linha, 'EMAIL', parteInfo, statusPorMessageId),
    ),
    conteudoHtml: primeira?.conteudoHtml ?? '',
    rodapeLinhas: [
      inst.orgao,
      `CNPJ ${inst.cnpj}`,
      inst.leiReferencia,
      `WhatsApp ${inst.contatoWhatsapp} · ${inst.contatoHorario}`,
    ],
  };
}

function montarMensagensEmail(
  dto: CertificadoAcessosDto,
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
  statusPorMessageId: Map<string, string>,
): MensagemEmailCertificado[] {
  const doCanal = dto.replicas.filter((r) => r.canal === 'EMAIL');
  return agruparPorConteudo(doCanal, (r) => `${r.assunto ?? ''}\n${r.conteudoHtml ?? ''}`).map(
    (grupo) => grupoParaEmail(grupo, dto.instituicao, parteInfo, statusPorMessageId),
  );
}

// ————— Correios —————

function rastreioLabel(envio: EnvioPostalDto, posicao: 'primeiro' | 'ultimo'): string {
  const evento =
    posicao === 'primeiro' ? envio.rastreio[0] : envio.rastreio[envio.rastreio.length - 1];
  if (!evento) return '';
  const detalhe = evento.detalhe ? ` | ${evento.detalhe}` : '';
  return `${evento.evento}: ${formatDateTimeAt(evento.dataHora)}${detalhe}`;
}

function cartaDoEnvio(
  dto: CertificadoAcessosDto,
  envio: EnvioPostalDto,
  requeridoNome: string,
): CartaCitacaoCertificado {
  const replica = dto.replicas.find(
    (r) => r.canal === 'CORREIOS' && r.parteArbitralId === envio.parteArbitralId,
  );
  return {
    requerente: dto.partes.requerentes[0]?.nome ?? '',
    requerido: requeridoNome,
    numeroProcesso: dto.processo.numeroProcesso ?? '',
    rito: dto.processo.rito ? rotuloRito(dto.processo.rito) : '',
    enderecoEntrega: envio.enderecoEntrega
      ? formatEndereco(envio.enderecoEntrega, 'certificado')
      : '',
    postagemLabel: rastreioLabel(envio, 'primeiro'),
    entregaLabel: STATUS_CORREIOS_LABELS[envio.statusCorreios] ?? envio.statusCorreios,
    conteudoHtml: replica?.conteudoHtml ?? '',
  };
}

function montarCorreios(
  dto: CertificadoAcessosDto,
  parteInfo: Map<string, { nome: string; papelLabel: string }>,
): EnvioCorreiosCertificado[] {
  return dto.correios.map((envio) => {
    const nome = parteInfo.get(envio.parteArbitralId)?.nome ?? '';
    return {
      destinatarioNome: nome,
      enderecoEntrega: envio.enderecoEntrega
        ? formatEndereco(envio.enderecoEntrega, 'certificado')
        : '',
      carta: cartaDoEnvio(dto, envio, nome),
      comprovantePostagem: {
        titulo: 'Comprovante de postagem — Correios',
        disponivel: Boolean(envio.comprovantePostagemUrl),
        descricao: envio.trackingCode
          ? `Comprovante de postagem nos Correios, com código de rastreio ${envio.trackingCode}.`
          : 'Comprovante de postagem nos Correios.',
        imagemUrl: envio.comprovantePostagemUrl,
      },
      avisoRecebimento: {
        titulo: 'Aviso de Recebimento (AR)',
        disponivel: envio.arDisponivel,
        descricao: envio.arDisponivel
          ? 'AR assinado pelo destinatário, digitalizado pelos Correios como prova de entrega.'
          : 'AR ainda não digitalizado pelos Correios para este envio.',
        imagemUrl: envio.arUrl,
      },
    };
  });
}

// ————— Entrada —————

export function mapCertificadoAcessos(dto: CertificadoAcessosDto): CertificadoAcessosArbitral {
  const todasPartes = [...dto.partes.requerentes, ...dto.partes.requeridos];
  const parteInfo = new Map(
    todasPartes.map((p) => [
      p.parteArbitralId,
      { nome: p.nome, papelLabel: PAPEL_LABEL[p.papel as keyof typeof PAPEL_LABEL] ?? p.papel },
    ]),
  );
  const statusPorMessageId = indexarStatusPorMessageId(dto.auditoria);
  // Uma fonte só para o href e para o texto exibido: derivar cada um do DTO deixaria o documento
  // mostrar um endereço que o link não abre.
  const urlValidacao = urlValidacaoSegura(dto.certificado.urlValidacao);

  return {
    processoId: dto.processo.id,
    numeroProcesso: dto.processo.numeroProcesso ?? '',
    dataEmissao: dto.certificado.dataEmissao,
    codigoAutenticacao: dto.certificado.codigoAutenticacao,
    urlAutenticacao: urlValidacao,
    urlAutenticacaoExibicao: urlValidacao ? urlExibicao(urlValidacao) : null,
    hashCertificado: dto.certificado.hashCertificado,
    versaoHash: dto.certificado.versaoHash,
    cadeiaIntegra: dto.certificado.cadeiaIntegra ?? null,
    cadeiaQuebradaNaVersao: dto.certificado.cadeiaQuebradaNaVersao ?? null,
    gruposPartes: montarGrupos(dto),
    objetivoParagrafos: dto.textos.objetivo,
    textosSeguranca: dto.textos.seguranca,
    resumoComprovacao: montarResumo(dto.comprovacao),
    canaisAuditoria: montarAuditoria(dto.auditoria, parteInfo),
    acessosPlataforma: montarAcessos(dto, parteInfo),
    mensagensWhatsapp: montarMensagensWhatsapp(
      dto.replicas,
      parteInfo,
      statusPorMessageId,
      dto.instituicao.orgao,
    ),
    respostasWhatsapp: montarRespostasWhatsapp(dto.respostas ?? [], parteInfo),
    mensagensEmail: montarMensagensEmail(dto, parteInfo, statusPorMessageId),
    enviosCorreios: montarCorreios(dto, parteInfo),
    instituicao: dto.instituicao,
  };
}
